import { Download } from 'lucide-react';
import { downloadLatestInstaller, RELEASES_LATEST_URL } from './downloadApp';
import type { Go, MarketingView } from './types';

const COLUMNS: { heading: string; links: { v: MarketingView | 'auth'; label: string }[] }[] = [
  {
    heading: 'Product',
    links: [
      { v: 'features', label: 'Features' },
      { v: 'pricing', label: 'Pricing' },
      { v: 'auth', label: 'Get started' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { v: 'about', label: 'About' },
      { v: 'auth', label: 'Sign in' },
    ],
  },
];

/** Marketing-site footer: brand line, link columns, copyright. */
export function MarketingFooter({ go }: { go: Go }) {
  return (
    <footer className="border-t border-border bg-bg-subtle">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-4">
        <div className="md:col-span-2">
          <button
            type="button"
            onClick={() => go('home')}
            className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="SpecForma home"
          >
            <span className="flex size-8 items-center justify-center rounded-md bg-primary text-inverse">
              <span className="text-xs font-bold">SF</span>
            </span>
            <span className="text-lg font-semibold tracking-tight">SpecForma</span>
          </button>
          <p className="mt-3 max-w-xs text-md text-muted">
            The specification platform for architecture &amp; engineering firms — write, format, and
            issue project manuals with confidence.
          </p>
          <a
            href={RELEASES_LATEST_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              // Plain clicks download in place; modified clicks (new tab,
              // copy link) keep the releases page via the real href.
              if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0)
                return;
              e.preventDefault();
              void downloadLatestInstaller();
            }}
            className="mt-4 inline-flex items-center gap-2 rounded-md text-md font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Download className="size-4" aria-hidden="true" />
            Download desktop app (Windows)
          </a>
        </div>

        {COLUMNS.map((col) => (
          <nav key={col.heading} aria-label={col.heading}>
            <h2 className="text-md font-semibold text-text">{col.heading}</h2>
            <ul className="mt-3 flex flex-col gap-2">
              {col.links.map((l) => (
                <li key={l.label}>
                  <button
                    type="button"
                    onClick={() => go(l.v)}
                    className="rounded text-md text-muted transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-6 text-md text-muted sm:px-6">
          © 2026 SpecForma. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
